/**
 * Горизонтальный рейтинг: сравнение величин, низкое → высокое.
 *
 * Форма выбрана по задаче данных, а не по вкусу: сравнение величин — это
 * столбцы, а последовательная шкала (один тон, больше → темнее) читается
 * надёжнее категориальной, когда серии не являются предметом разговора.
 *
 * Горизонтальные, потому что подписи — русские названия отделов и проектов:
 * в вертикальных они наезжают друг на друга или встают вертикально.
 *
 * Значение подписано прямо у столбца: контраст палитры к поверхности ниже 3:1,
 * и правило рельефа требует видимых подписей. Плюс табличный вид.
 */

import { useState } from "react";

import { Button } from "../Button";
import { cn } from "../cn";

export interface RankingRow {
  id: string;
  label: string;
  value: number;
  /** Подпись под названием: доля, срок, что угодно поясняющее. */
  hint?: string;
  onSelect?: () => void;
}

interface BarRankingProps {
  title: string;
  rows: RankingRow[];
  unit?: string;
  /** Названия колонок в табличном виде. */
  valueHeader?: string;
  emptyText?: string;
}

/** Пять шагов последовательной шкалы: больше значение — темнее. */
function stepFor(value: number, max: number): string {
  if (max <= 0) return "var(--seq-1)";
  const ratio = value / max;
  if (ratio > 0.8) return "var(--seq-5)";
  if (ratio > 0.6) return "var(--seq-4)";
  if (ratio > 0.4) return "var(--seq-3)";
  if (ratio > 0.2) return "var(--seq-2)";
  return "var(--seq-1)";
}

export function BarRanking({
  title,
  rows,
  unit = "",
  valueHeader = "Значение",
  emptyText = "Нет данных за выбранный период",
}: BarRankingProps) {
  const [asTable, setAsTable] = useState(false);
  const max = Math.max(0, ...rows.map((row) => row.value));

  return (
    <figure
      // Имя задаётся явно: figcaption содержит и заголовок, и кнопку
      // переключения вида, и доступное имя из него получилось бы
      // «Название Таблицей».
      aria-label={title}
      className="viz m-0 flex flex-col gap-2.5 rounded-lg border border-line bg-surface p-4"
    >
      <figcaption className="flex items-center justify-between gap-3">
        <h3 className="text-[13px] font-extrabold text-fg">{title}</h3>
        <Button size="sm" variant="ghost" onClick={() => setAsTable((value) => !value)}>
          {asTable ? "Диаграмма" : "Таблицей"}
        </Button>
      </figcaption>

      {rows.length === 0 ? (
        <p className="py-6 text-center text-[13px] text-faint">{emptyText}</p>
      ) : asTable ? (
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead>
              <tr className="border-b border-line">
                <th scope="col" className="py-1.5 text-left text-[11px] font-bold text-faint uppercase">
                  Название
                </th>
                <th scope="col" className="py-1.5 text-right text-[11px] font-bold text-faint uppercase">
                  {valueHeader}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-line last:border-0">
                  <td className="py-1.5 text-fg">{row.label}</td>
                  <td className="py-1.5 text-right text-fg tabular-nums">
                    {row.value}
                    {unit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <ul className="flex flex-col gap-2">
          {rows.map((row) => {
            const width = max === 0 ? 0 : Math.max(2, (row.value / max) * 100);
            const Wrapper = row.onSelect ? "button" : "div";
            return (
              <li key={row.id}>
                <Wrapper
                  {...(row.onSelect ? { type: "button" as const, onClick: row.onSelect } : {})}
                  className={cn(
                    "flex w-full flex-col gap-1 text-left",
                    row.onSelect && "group cursor-pointer",
                  )}
                >
                  <span className="flex items-baseline justify-between gap-3">
                    <span
                      className={cn(
                        "min-w-0 truncate text-[13px] text-fg",
                        row.onSelect && "group-hover:underline",
                      )}
                    >
                      {row.label}
                    </span>
                    {/* Подпись значения обязательна: правило рельефа. */}
                    <span className="shrink-0 text-[13px] font-semibold text-fg tabular-nums">
                      {row.value}
                      {unit}
                    </span>
                  </span>

                  <span className="flex items-center gap-2">
                    <span className="h-2 flex-1 overflow-hidden rounded-full bg-sunken">
                      <span
                        className="block h-full rounded-full"
                        style={{ width: `${String(width)}%`, background: stepFor(row.value, max) }}
                      />
                    </span>
                  </span>

                  {row.hint !== undefined && (
                    <span className="text-[11px] text-faint">{row.hint}</span>
                  )}
                </Wrapper>
              </li>
            );
          })}
        </ul>
      )}
    </figure>
  );
}
